import {Database} from '@tnc/shared';

import {useNotifications} from '@/providers/components/notificationsProvider';

import Page from '@/components/Page';
import {DataTableBasic} from '@/components/dataTableBasic';
import {DataTableView} from '@/components/dataTableView';
import DefaultTableAvatarImage from '@/components/defaultTableAvatarImage';

import {useCreateRetailOrder} from '../providers/createRetailOrderProvider';
import RetailOrderDetailComponent from './retailOrderDetailComponent';

function CreateRetailOrderForm() {
  // Hooks
  const {toast} = useNotifications();
  const {
    markets,
    orderItems,
    selectedDistributorId,
    updateOrderItem,
    removeOrderItem,
    selectedProductIds,
    acknowledging,
    setAcknowledging,
  } = useCreateRetailOrder();

  const createOrderItemFromRow = (
    row: Database['public']['Views']['retail_order_available_items']['Row'],
    quantity: number
  ) => {
    if (!row.fk_product_id || !row.item_count) {
      toast.error('Invalid item data');
      return null;
    }

    // Create a properly typed order item
    return {
      fk_product_id: row.fk_product_id,
      fk_distributor_id: row.fk_distributor_id || 0,
      fk_brand_id: row.fk_brand_id || 0,
      fk_market_id: row.fk_market_id || 0,
      product_name: row.product_name || '',
      brand_name: row.brand_name || '',
      distributor_name: row.distributor_name || '',
      product_logo: row.product_logo || '',
      brand_logo: row.brand_logo || '',
      item_count: row.item_count,
      quantity: quantity,
    };
  };

  if (acknowledging) {
    return <RetailOrderDetailComponent />;
  }

  return (
    <Page currentPage="Retailer Orders">
      {/* AVAILABLE ITEMS FOR ORDER */}
      <DataTableView
        view="retail_order_available_items"
        columnsDisplay={[
          {
            header: 'Product',
            field: 'product_name',
            render: row => (
              <div className="flex min-w-[150px] gap-2">
                <DefaultTableAvatarImage src={row.product_logo || row.brand_logo || ''} alt="Product Logo" />
                <div className="flex flex-col">
                  <span>{row.product_name}</span>
                  <span className="text-muted text-truncate text-gray text-xs text-textmuted">{row.brand_name}</span>
                </div>
              </div>
            ),
          },
          {
            header: 'Distributor',
            field: 'distributor_name',
          },
          {
            header: 'Market',
            field: 'market_name',
          },
          {
            header: 'Item Count',
            field: 'item_count',
          },
        ]}
        sortOptions={[
          {
            column: 'brand_name',
            direction: 'asc',
            label: 'Item Name Ascending',
            default: true,
          },
        ]}
        filters={[
          {
            column: 'fk_market_id' as const,
            operator: 'in' as const,
            value: markets.map(m => m.fk_market_id),
          },
          ...(selectedDistributorId
            ? [
                {
                  column: 'fk_distributor_id' as const,
                  operator: 'eq' as const,
                  value: selectedDistributorId,
                },
              ]
            : []),
          ...(selectedProductIds.size > 0
            ? [
                {
                  not: {
                    column: 'fk_product_id' as const,
                    operator: 'in' as const,
                    value: Array.from(selectedProductIds),
                  },
                },
              ]
            : []),
        ]}
        searchColumn="product_name"
        actionsDisplay={[
          {
            icon: 'ri-add-line',
            color: 'primary',
            handler: row => {
              if (!row.fk_product_id || !row.item_count) {
                toast.error('Invalid item data');
                return;
              }
              const orderItem = createOrderItemFromRow(row, 1);
              if (orderItem) {
                updateOrderItem(row.fk_product_id, orderItem);
              }
            },
            permission: () => true,
          },
        ]}
        title="Items with Distributor Orders"
        hideAdd
      />

      {/* ORDER OVERVIEW */}
      <DataTableBasic
        title="Order Overview"
        data={Object.values(orderItems)}
        columns={[
          {
            header: 'Product',
            field: 'product_name',
            render: row => (
              <div className="flex min-w-[150px] gap-2">
                <DefaultTableAvatarImage src={row.product_logo || row.brand_logo || ''} alt="Product Logo" />
                <div className="flex flex-col">
                  <span>{row.product_name}</span>
                  <span className="text-muted text-truncate text-gray text-xs text-textmuted">{row.brand_name}</span>
                </div>
              </div>
            ),
          },
          {
            header: 'Quantity',
            field: 'quantity',
          },
          {
            header: 'Distributor',
            field: 'distributor_name',
          },
        ]}
        actions={[
          {
            icon: 'ri-add-line',
            color: 'primary',
            permission: row => {
              const currentQty = orderItems[row.fk_product_id]?.quantity || 0;
              return currentQty < (row.item_count || 0);
            },
            handler: row => {
              if (!row.fk_product_id || !row.item_count) return;
              const currentQty = orderItems[row.fk_product_id]?.quantity || 0;
              if (currentQty < row.item_count) {
                const orderItem = createOrderItemFromRow(row, currentQty + 1);
                if (orderItem) {
                  updateOrderItem(row.fk_product_id, orderItem);
                }
              }
            },
            render: row => (
              <div className="input-group !flex-nowrap rounded-md border dark:border-defaultborder/10">
                <button
                  aria-label="decrease"
                  type="button"
                  className="ti-btn ti-btn-icon input-group-text flex-fill product-quantity-minus !mb-0 !rounded-e-none !bg-primary !text-white"
                  onClick={() => {
                    if (!row.fk_product_id) return;
                    const currentQty = orderItems[row.fk_product_id]?.quantity || 0;
                    if (currentQty > 1) {
                      const orderItem = createOrderItemFromRow(row, currentQty - 1);
                      if (orderItem) {
                        updateOrderItem(row.fk_product_id, orderItem);
                      }
                    }
                  }}
                >
                  <i className="ri-subtract-line"></i>
                </button>
                <input
                  type="text"
                  className="form-control !w-12 border-0 !p-1 text-center"
                  value={orderItems[row.fk_product_id]?.quantity || 0}
                  onChange={e => {
                    if (!row.fk_product_id || !row.item_count) return;
                    const newQty = parseInt(e.target.value) || 0;
                    if (newQty <= row.item_count) {
                      const orderItem = createOrderItemFromRow(row, newQty);
                      if (orderItem) {
                        updateOrderItem(row.fk_product_id, orderItem);
                      }
                    }
                  }}
                />
                <button
                  aria-label="increase"
                  type="button"
                  className="ti-btn ti-btn-icon input-group-text flex-fill product-quantity-plus !mb-0 !rounded-s-none !bg-primary !text-white"
                  onClick={() => {
                    if (!row.fk_product_id || !row.item_count) return;
                    const currentQty = orderItems[row.fk_product_id]?.quantity || 0;
                    if (currentQty < row.item_count) {
                      const orderItem = createOrderItemFromRow(row, currentQty + 1);
                      if (orderItem) {
                        updateOrderItem(row.fk_product_id, orderItem);
                      }
                    }
                  }}
                >
                  <i className="ri-add-line"></i>
                </button>
              </div>
            ),
          },
          {
            icon: 'ri-delete-bin-line',
            color: 'danger',
            permission: () => true,
            handler: row => {
              if (row.fk_product_id) {
                removeOrderItem(row.fk_product_id);
              }
            },
          },
        ]}
        hideAdd
      />

      {/* SUBMIT ORDER */}
      <div className="flex justify-end">
        <button className="ti-btn ti-btn-primary" onClick={() => setAcknowledging(true)}>
          Review Order
        </button>
      </div>
    </Page>
  );
}

export default CreateRetailOrderForm;
